import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { IDashboardTabComponent } from "../Dashboard";
import PageContent from "@my-dld/components/PageContent";
import PageTitle from "@my-dld/components/PageTitle";
import Strings from "@my-dld/assets/strings";
import { MyRequestsBCItem } from "@my-dld/contexts/PageContext";
import { Complain, Notifier, NotifierTypeId, Participant, RVSLookUp, Transaction, TransactionStatusId } from "@eres/rvsapi/entities";
import { RVSLookUpName, rvsLookUpToStateKey } from "@eres/rvsapi/api";
import { RVSApi } from "@eres/rvsapi";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { I8N } from "@eres/core";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { openBinaryAsDocument } from "@my-dld/utils/Utils";
import { trackEventWithData } from "@my-dld/utils/Analytics";

interface Props extends RoutedViewProps<{ requestKey: string; showDownload: string }> {

}

interface State {
	claimantTypes: RVSLookUp[];
	complainTypes: RVSLookUp[];
	defendantTypes: RVSLookUp[];
	claimantType?: RVSLookUp;
	complainType?: RVSLookUp;
	defendantType?: RVSLookUp;
	complainCategory?: RVSLookUp;
	transaction?: Transaction;
}

const LookUpNames = [ RVSLookUpName.CLAIMANT_TYPES, RVSLookUpName.DEFENDANT_TYPES, RVSLookUpName.COMPLAIN_TYPES ];

class ComplaintDetails extends Component<Props, State> implements IDashboardTabComponent<Props, State> {
	constructor( props: Props ) {
		super( props );
		this.state = {
			claimantTypes: [],
			complainTypes: [],
			defendantTypes: [],
		};
	}

	componentDidMount() {
		showLoading();
		this.loadLookUps( 0 );
	}

	render() {
		const showDownload = this.props.match.params.showDownload;
		const { complainType, defendantType, complainCategory, transaction } = this.state;
		return <React.Fragment>
			<PageContent title={ Strings.complainant_details }>
				<PageTitle title={ Strings.home_Complaint } paths={ [ MyRequestsBCItem ] }/>
				{ transaction && <div className="alert alert-info">
					{ Strings.transaction_number }: { transaction!.TransactionNumber }</div> }
				<DefendantDetails
					participant={ getParticipant( transaction ) }
					defendant={ getNotifier( NotifierTypeId.Defendant, transaction ) }
					defendantType={ defendantType }/>
				<ComplainCategory
					complainType={ complainType }
					categoryType={ complainCategory }/>
				<ProjectUnitDetails complain={ getComplain( transaction ) }/>
				<RenderProcedures transaction={ transaction }/>
				{ showDownload === "true" && <div className="row">
					<div className="col-md-12">
						<div className="form-group">
							<button
								className="btn btn_1 btn_style1" type="submit"
								onClick={ this.onDownloadComplaint }>
								{ Strings.download_complaint }</button>
						</div>
					</div>
				</div> }
			</PageContent>
		</React.Fragment>;
	}

	private loadLookUps( index: number ) {
		if ( index < LookUpNames.length ) {
			const lookUpName = LookUpNames[ index ];
			RVSApi.loadLookUp( lookUpName ).then( ( resp: any ) => {
				const stateKey = rvsLookUpToStateKey( lookUpName );
				this.setState( {
					...this.state,
					[ stateKey ]: ( resp && resp.Lookups ) ? resp.Lookups : [],
				}, () => {
					this.loadLookUps( index + 1 );
				} );
			} ).catch( reason => {
				this.loadLookUps( index + 1 );
			} );
		} else {
			this.loadComplaintDetails();
		}
	}

	private loadComplaintDetails() {
		const reqKey = this.props.match.params.requestKey;
		RVSApi.loadComplaintDetails( reqKey ).then( ( response: any ) => {
			this.setState( { transaction: response }, () => {
				this.setLookupData( response );
			} );
		} ).catch( ( reason: any ) => {
			hideLoading();
			showErrorMessage( reason );
		} );
	}

	private setLookupData( transaction: Transaction ) {
		const { claimantTypes, defendantTypes, complainTypes } = this.state;
		const complain = getComplain( transaction );
		if ( complain ) {
			this.setState( {
				claimantType: claimantTypes.find( ( type: RVSLookUp ) => {
					return type.ID === complain.ComplainantTypeId;
				} ),
				defendantType: defendantTypes.find( ( type: RVSLookUp ) => {
					return type.ID === complain.DefendantTypeId;
				} ),
				complainType: complainTypes.find( ( type: RVSLookUp ) => {
					return type.ID === complain.ActivityId;
				} ),
			}, () => {
				this.loadComplainCategory( complain );
			} );
		}
	}

	private loadComplainCategory( complain: Complain ) {
		const { complainType } = this.state;
		if ( complainType ) {
			RVSApi.loadComplainCategoriesByType( complainType.ID ).then( ( response: any ) => {
				hideLoading();
				if ( response && response.Lookups ) {
					const categories: RVSLookUp[] = response.Lookups;
					this.setState( {
						complainCategory: categories.find( ( category ) => {
							return category.ID === complain.ComplainCategoryId;
						} ),
					} );
				}
			} ).catch( reason => {
				hideLoading();
			} );
		} else {
			hideLoading();
		}
	}

	private onDownloadComplaint = () => {
		const reqKey = this.props.match.params.requestKey;
		showLoading();
		RVSApi.downloadComplaint( reqKey ).then( ( response: any ) => {
			hideLoading();
			if ( response && response.Object ) {
				trackEventWithData( "RVS", "DownloadComplaintSuccess", { requestKey: reqKey } );
				openBinaryAsDocument( response.Object, "Download Complaint", "application/pdf" );
			}
		} ).catch( ( reason ) => {
			trackEventWithData( "RVS", "DownloadComplaintFailed", { requestKey: reqKey } );
			hideLoading();
			showErrorMessage( reason );
		} );
	};
}

function DefendantDetails( props: { participant?: Participant, defendant?: Notifier, defendantType?: RVSLookUp } ) {
	const { participant, defendant, defendantType } = props;
	return <div id="RVSComplaintCard1">
		<div className="section-title mb-3">
			<h5>{ Strings.defendant_details }</h5>
		</div>
		<div className="flex-grid">
			<div>
				<span>{ Strings.company_name }</span>
				<span>{ getDefenderName( participant ) }</span>
			</div>
			<div>
				<span>{ Strings.Mobile_text }</span>
				<span>{ getNotifierMobile( defendant ) }</span>
			</div>
			<div>
				<span>{ Strings.email }</span>
				<span>{ getNotifierEmail( defendant ) }</span>
			</div>
			<div>
				<span>{ Strings.defendant_type }</span>
				<span>{ defendantType ? ( I8N.isRTL() ? defendantType.NameAr : defendantType.NameEn ) : "" }</span>
			</div>
		</div>
	</div>;
}

function ComplainCategory( props: { complainType?: RVSLookUp, categoryType?: RVSLookUp } ) {
	const { complainType, categoryType } = props;
	return <div id="RVSComplaintCard2">
		<div className="section-title mb-3">
			<h5>{ Strings.complain_category }</h5>
		</div>
		<div className="flex-grid">
			<div>
				<span>{ Strings.complain_type }</span>
				<span>{ complainType ? ( I8N.isRTL() ? complainType.NameAr : complainType.NameEn ) : "" }</span>
			</div>
			<div>
				<span>{ Strings.complain_category }</span>
				<span>{ categoryType ? ( I8N.isRTL() ? categoryType.NameAr : categoryType.NameEn ) : "" }</span>
			</div>
		</div>
	</div>;
}

function ProjectUnitDetails( props: { complain?: Complain } ) {
	const { complain } = props;
	if ( canShowProjectUnitDetails( complain ) ) {
		return <div id="RVSComplaintCard3">
			<div className="section-title mb-3">
				<h5>{ Strings.project_or_unit_details }</h5>
			</div>
			<div className="flex-grid">
				{ !isEmptyStringOrNull( complain!.ProjectName ) && <div>
					<span>{ Strings.Project_Name_Label }</span>
					<span>{ complain!.ProjectName }</span>
				</div> }
				{ !isEmptyStringOrNull( complain!.PlotNumber ) && <div>
					<span>{ Strings.plot_number }</span>
					<span>{ complain!.PlotNumber }</span>
				</div> }
				{ !isEmptyStringOrNull( complain!.BuildingNumber ) && <div>
					<span>{ Strings.building_number }</span>
					<span>{ complain!.BuildingNumber }</span>
				</div> }
				{ !isEmptyStringOrNull( complain!.UnitNumber ) && <div>
					<span>{ Strings.Unit_Number_Label }</span>
					<span>{ complain!.UnitNumber }</span>
				</div> }
				{ !isEmptyStringOrNull( complain!.PropertyType ) && <div>
					<span>{ Strings.property_type }</span>
					<span>{ complain!.PropertyType }</span>
				</div> }
				{ !isEmptyStringOrNull( complain!.Community ) && <div>
					<span>{ Strings.community }</span>
					<span>{ complain!.Community }</span>
				</div> }
			</div>
		</div>;
	}
	return null;
}

function RenderProcedures( props: { transaction?: Transaction } ) {
	const { transaction } = props;
	if ( transaction && transaction.TransactionService &&
		transaction.TransactionService.TransactionStatusId === TransactionStatusId.Closed ) {
		let reason = "";
		if ( transaction )
			reason = I8N.isRTL() ? transaction!.CloseReasonNameAr! : transaction!.CloseReasonNameEn!;
		return <div id="RVSComplaintCard3">
			<div className="section-title mb-3">
				<h5>{ Strings.procedures }</h5>
			</div>
			<div className="card card-body mb-4 p-3">
				<span>{ reason }</span>
			</div>
		</div>;
	}
	return null;
}

function getComplain( transaction?: Transaction ) {
	if ( transaction ) {
		return transaction.Complain;
	}
	return undefined;
}

function getParticipant( transaction?: Transaction ) {
	if ( transaction && transaction.Participants && transaction.Participants.length > 0 ) {
		return transaction.Participants[ 0 ];
	}
	return undefined;
}

export function getNotifier( notifierId: number, transaction?: Transaction ) {
	if ( transaction ) {
		return transaction.Notifiers.find( ( notifier: Notifier ) => {
			return notifier.NotifierTypeId === notifierId;
		} );
	}
	return undefined;
}

function getDefenderName( participant?: Participant ) {
	if ( participant ) {
		return I8N.isRTL() ? participant.NameAr : participant.NameEn;
	}
	return "";
}

function getNotifierMobile( notifier?: Notifier ) {
	if ( notifier ) {
		return notifier.NotifierMobile;
	}
	return "";
}

function getNotifierEmail( notifier?: Notifier ) {
	if ( notifier ) {
		return notifier.NotifierEmail;
	}
	return "";
}

function canShowProjectUnitDetails( complain?: Complain ) {
	if ( complain ) {
		return !isEmptyStringOrNull( complain.ProjectName ) || !isEmptyStringOrNull( complain.PlotNumber ) ||
			!isEmptyStringOrNull( complain.BuildingNumber ) || !isEmptyStringOrNull( complain.UnitNumber ) ||
			!isEmptyStringOrNull( complain.PropertyType ) || !isEmptyStringOrNull( complain.Community );
	}
	return false;
}

export default ComplaintDetails;
